import React, { useState } from 'react';

/**
 * Création d'un formulaire de satisfaction.
 *
 * Trois décisions, prises AVANT d'écrire la moindre question : pour quelle
 * concession, et surtout **à qui** le questionnaire s'adressera. Ce dernier
 * choix commandait tout le reste (délai d'envoi, quota, anti-doublon, jusqu'aux
 * variables disponibles dans le message) et il était jusqu'au 2026-09-17 caché
 * au milieu de l'éditeur, sous la liste des questions : on créait un formulaire
 * sans jamais voir qu'il fallait le brancher sur une source.
 */

// Chaque source est décrite en une phrase : « manuel » ou « csv » ne dit rien
// à quelqu'un qui découvre l'écran.
const EXPLICATIONS = {
  manuel: 'Vous envoyez le questionnaire client par client, depuis cet écran. Aucun envoi automatique.',
  csv: 'Vous déposez un fichier d\'export ; l\'écran Campagnes contrôle puis envoie.',
  crm_passages_atelier: 'Automatique : les clients facturés à l\'atelier, repris du CRM, quelques jours après leur passage.',
  a97_sellens_ventes_vn: 'Automatique : les acheteurs de véhicules neufs SELLENS, repris de l\'entrepôt A97 DATA, quelques jours après la vente.',
  a97_sellens_passages_atelier: 'Automatique : les clients facturés dans les ateliers SELLENS, repris de l\'entrepôt A97 DATA, quelques jours après leur passage.',
};

export default function SatisfactionNouveauModal({ user, concessions, catalogue, onClose, onCreer }) {
  const choixConcession = !user?.concession_id && (concessions || []).length > 1;
  const [nom, setNom] = useState('');
  const [concessionId, setConcessionId] = useState(
    user?.concession_id || (concessions || []).length === 1 ? String(user?.concession_id || concessions[0]?.id) : ''
  );
  const [source, setSource] = useState('manuel');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const sources = catalogue?.sources || [];

  const valider = async () => {
    if (!nom.trim()) { setErreur('Donnez un nom au formulaire.'); return; }
    if (choixConcession && !concessionId) { setErreur('Choisissez la concession.'); return; }
    setEnCours(true);
    setErreur('');
    try {
      await onCreer({ nom: nom.trim(), concession_id: concessionId ? Number(concessionId) : undefined, source });
    } catch (e) {
      setErreur(e.response?.data?.error || 'Création impossible');
      setEnCours(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nouveau formulaire</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body sat-creation">
          {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

          <label className="sat-champ sat-champ-large">
            <span>Nom du formulaire</span>
            <input className="sat-input" value={nom} autoFocus
              placeholder="ex. Satisfaction après livraison VN"
              onChange={(e) => setNom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') valider(); }} />
          </label>

          {choixConcession && (
            <label className="sat-champ sat-champ-large">
              <span>Concession</span>
              <select className="sat-input" value={concessionId} onChange={(e) => setConcessionId(e.target.value)}>
                <option value="">Choisir…</option>
                {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </label>
          )}

          <div className="sat-champ sat-champ-large">
            <span>À qui ce questionnaire sera-t-il envoyé ?</span>
            <div className="sat-sources">
              {sources.map((s) => (
                <label key={s.cle} className={`sat-source${source === s.cle ? ' sat-source-on' : ''}`}>
                  <input type="radio" name="sat-source" value={s.cle}
                    checked={source === s.cle} onChange={() => setSource(s.cle)} />
                  <span>
                    <b>{s.label}</b>
                    <small>{EXPLICATIONS[s.cle] || ''}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="sat-aide">
            Ce choix se modifie ensuite à tout moment dans le formulaire. Rien ne part tant que
            vous n'avez pas ajouté de questions et basculé le formulaire en envoi réel.
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={valider} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer et configurer'}
          </button>
        </div>
      </div>
    </div>
  );
}
